import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Droplet, Plus, Trash2, Edit2, FileText, Download,
  Truck, Building2, Calendar, Weight, X, Check, AlertCircle,
  ChevronDown, ChevronUp, Eye, RefreshCw, Camera, Loader2, Sparkles,
  BarChart3, Flame, ShieldCheck
} from 'lucide-react';
import { getApiHeaders } from '../../utils/apiHelpers';
import { oliApi, erroreOli, urlDocumento } from './oli/api';
import { BadgeAutorizzazione } from './oli/Badge';
import { RitiroCard } from './oli/RitiroCard';
import { BannerVerifiche, VerificheOli } from './oli/VerificheOli';
import { RegistroFriggitrici } from './oli/RegistroFriggitrici';
import { CopiaFirmataDialog } from './oli/CopiaFirmataDialog';
import { CorreggiRitiroDialog } from './oli/CorreggiRitiroDialog';
import { AnnullaDialog } from './oli/AnnullaDialog';

const API = process.env.REACT_APP_BACKEND_URL;
const TRASPORTATORE_VUOTO = { denominazione: '', codice_fiscale: '', partita_iva: '', numero_iscrizione_albo: '',
  albo_categoria: '', albo_scadenza: '', indirizzo: '', telefono: '', email: '', note: '' };
const DESTINATARIO_VUOTO = { denominazione: '', codice_fiscale: '', partita_iva: '', indirizzo: '', cap: '', comune: '', provincia: '',
  numero_autorizzazione: '', autorizzazione_ente: '', autorizzazione_scadenza: '', operazione_recupero: 'R13', note: '' };
const senzaVuoti = (o) => Object.fromEntries(Object.entries(o).map(([k, v]) => [k, v === '' ? null : v]));
// In modifica si portano tutti i campi: il salvataggio sostituisce la scheda intera
const perModifica = (vuoto, item) => Object.fromEntries(Object.keys(vuoto).map((k) => [k, item[k] ?? vuoto[k]]));
const CAMPO_DATA = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500';
const TABS = [
  { id: 'prelievi', label: 'Ritiri', icon: Droplet },
  { id: 'friggitrici', label: 'Friggitrici', icon: Flame },
  { id: 'trasportatori', label: 'Trasportatori', icon: Truck },
  { id: 'destinatari', label: 'Impianti', icon: Building2 },
  { id: 'verifiche', label: 'Controllo', icon: ShieldCheck },
  { id: 'statistiche', label: 'Statistiche', icon: BarChart3 },
];

const OliEsaustiSection = () => {
  // Stati principali
  const [activeTab, setActiveTab] = useState('prelievi'); // prelievi, friggitrici, trasportatori, destinatari, verifiche, statistiche
  const [loading, setLoading] = useState(true);
  const [statistiche, setStatistiche] = useState(null);
  const [responsabile, setResponsabile] = useState(false);
  const [aggiorna, setAggiorna] = useState(0);
  const [copiaPer, setCopiaPer] = useState(null);
  const [correggiPer, setCorreggiPer] = useState(null);
  const [annullaPer, setAnnullaPer] = useState(null);
  
  // Statistiche dettagliate
  const [statsDettaglio, setStatsDettaglio] = useState(null);
  const [statsAnno, setStatsAnno] = useState(new Date().getFullYear());
  const [loadingStats, setLoadingStats] = useState(false);
  
  // Visualizzazione documento
  const [showDocumento, setShowDocumento] = useState(null);
  
  // Dati
  const [prelievi, setPrelievi] = useState([]);
  const [trasportatori, setTrasportatori] = useState([]);
  const [destinatari, setDestinatari] = useState([]);
  
  // Modal
  const [showModal, setShowModal] = useState(null); // 'prelievo', 'trasportatore', 'destinatario', 'scan-fir'
  const [editingItem, setEditingItem] = useState(null);
  const [showDettaglio, setShowDettaglio] = useState(null);
  
  // OCR Scan
  const [scanningFir, setScanningFir] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [scanPreview, setScanPreview] = useState(null);
  const [scannedFile, setScannedFile] = useState(null);
  
  // Form prelievo
  const [formPrelievo, setFormPrelievo] = useState({
    data_prelievo: new Date().toISOString().split('T')[0],
    trasportatore_id: '',
    destinatario_id: '',
    quantita_kg: '',
    numero_fir: '',
    conducente_nome: '',
    conducente_cognome: '',
    targa_automezzo: '',
    note: ''
  });
  
  // Form trasportatore
  const [formTrasportatore, setFormTrasportatore] = useState(TRASPORTATORE_VUOTO);
  
  // Form destinatario
  const [formDestinatario, setFormDestinatario] = useState(DESTINATARIO_VUOTO);

  const tenantDb = localStorage.getItem('tenant_db');
  const headers = {
    ...getApiHeaders(),
    'Content-Type': 'application/json'
  };

  // Caricamento dati
  useEffect(() => {
    loadData();
    oliApi.get('/permessi').then((p) => setResponsabile(!!p.responsabile)).catch(() => setResponsabile(false));
  }, []);

  const loadData = async () => {
    setAggiorna((a) => a + 1);
    setLoading(true);
    try {
      const [prelieviRes, trasportatoriRes, destinatariRes, statsRes] = await Promise.all([
        fetch(`${API}/api/gestione/haccp/olio-esausto/prelievi`, { headers }),
        fetch(`${API}/api/gestione/haccp/olio-esausto/trasportatori`, { headers }),
        fetch(`${API}/api/gestione/haccp/olio-esausto/destinatari`, { headers }),
        fetch(`${API}/api/gestione/haccp/olio-esausto/statistiche`, { headers })
      ]);
      
      const prelieviData = await prelieviRes.json();
      const trasportatoriData = await trasportatoriRes.json();
      const destinatariData = await destinatariRes.json();
      const statsData = await statsRes.json();
      
      setPrelievi(prelieviData.prelievi || []);
      setTrasportatori(trasportatoriData.trasportatori || []);
      setDestinatari(destinatariData.destinatari || []);
      setStatistiche(statsData.statistiche || null);
    } catch (error) {
      console.error('Errore caricamento dati:', error);
    }
    setLoading(false);
  };

  // Carica statistiche dettagliate per anno
  const loadStatsDettaglio = async (anno) => {
    setLoadingStats(true);
    try {
      const response = await fetch(`${API}/api/gestione/haccp/olio-esausto/statistiche/dettaglio?anno=${anno}`, {
        headers: { 'X-Tenant-DB': tenantDb }
      });
      const data = await response.json();
      if (data.success) {
        setStatsDettaglio(data);
      }
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
    setLoadingStats(false);
  };

  // Effetto per caricare stats dettaglio quando cambia anno o tab
  useEffect(() => {
    if (activeTab === 'statistiche') {
      loadStatsDettaglio(statsAnno);
    }
  }, [activeTab, statsAnno]);

  // CRUD Prelievi
  const salvaPrelievo = async () => {
    try {
      const res = await oliApi.post('/prelievi', {
        ...formPrelievo,
        quantita_kg: parseFloat(formPrelievo.quantita_kg)
      });
      toast.success(`Ritiro registrato (n. ${res.prelievo.numero_registro})`);
      (res.avvisi || []).forEach((a) => toast.warning(a));
      setShowModal(null);
      setFormPrelievo({
        data_prelievo: new Date().toISOString().split('T')[0],
        trasportatore_id: '',
        destinatario_id: '',
        quantita_kg: '',
        numero_fir: '',
        conducente_nome: '',
        conducente_cognome: '',
        targa_automezzo: '',
        note: ''
      });
      loadData();
    } catch (error) {
      toast.error(erroreOli(error, 'Ritiro non salvato'));
    }
  };

  // CRUD Trasportatori
  const salvaTrasportatore = async () => {
    try {
      const method = editingItem ? 'PUT' : 'POST';
      const url = editingItem 
        ? `${API}/api/gestione/haccp/olio-esausto/trasportatori/${editingItem.id}`
        : `${API}/api/gestione/haccp/olio-esausto/trasportatori`;
      
      const response = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(senzaVuoti(formTrasportatore))
      });
      
      if (response.ok) {
        setShowModal(null);
        setEditingItem(null);
        setFormTrasportatore(TRASPORTATORE_VUOTO);
        loadData();
      } else {
        toast.error('Trasportatore non salvato: controlla i campi obbligatori');
      }
    } catch (error) {
      console.error('Errore salvataggio:', error);
    }
  };

  const eliminaTrasportatore = async (id) => {
    if (!window.confirm('Disattivare questo trasportatore? I ritiri passati restano nel registro.')) return;
    try {
      await oliApi.del(`/trasportatori/${id}`);
      loadData();
    } catch (error) {
      toast.error(erroreOli(error, 'Trasportatore non disattivato'));
    }
  };

  const disattivaImpianto = async (id) => {
    if (!window.confirm('Disattivare questo impianto? I ritiri passati restano nel registro.')) return;
    try {
      await oliApi.del(`/destinatari/${id}`);
      loadData();
    } catch (error) {
      toast.error(erroreOli(error, 'Impianto non disattivato'));
    }
  };

  // CRUD Destinatari (impianti)
  const salvaDestinatario = async () => {
    try {
      const response = await fetch(editingItem
        ? `${API}/api/gestione/haccp/olio-esausto/destinatari/${editingItem.id}`
        : `${API}/api/gestione/haccp/olio-esausto/destinatari`, {
        method: editingItem ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(senzaVuoti(formDestinatario))
      });
      
      if (response.ok) {
        setShowModal(null);
        setEditingItem(null);
        setFormDestinatario(DESTINATARIO_VUOTO);
        loadData();
      } else {
        toast.error('Impianto non salvato: controlla i campi obbligatori');
      }
    } catch (error) {
      console.error('Errore salvataggio:', error);
    }
  };

  // Upload documento FIR
  const uploadDocumentoFir = async (prelievoId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      await oliApi.post(`/prelievi/${prelievoId}/documento`, formData);
      toast.success('FIR caricato');
      loadData();
    } catch (error) {
      toast.error(erroreOli(error, 'FIR non caricato'));
    }
  };

  // Scansiona documento FIR con OCR
  const handleScanFir = async (file) => {
    if (!file) return;
    
    setScanningFir(true);
    setScanError(null);
    setScannedFile(file);
    
    // Mostra preview
    const reader = new FileReader();
    reader.onload = (e) => setScanPreview(e.target.result);
    reader.readAsDataURL(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API}/api/gestione/haccp/olio-esausto/process-fir-document`, {
        method: 'POST',
        headers: { 'X-Tenant-DB': tenantDb },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Errore server: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.extracted_data) {
        const ed = data.extracted_data;
        
        // Auto-popola il form prelievo con controlli null-safe
        setFormPrelievo(prev => ({
          ...prev,
          data_prelievo: ed.data_emissione || prev.data_prelievo,
          numero_fir: ed.numero_fir || prev.numero_fir,
          quantita_kg: ed.rifiuto?.quantita_kg != null ? String(ed.rifiuto.quantita_kg) : prev.quantita_kg,
          conducente_nome: ed.trasportatore?.conducente_nome || prev.conducente_nome,
          conducente_cognome: ed.trasportatore?.conducente_cognome || prev.conducente_cognome,
          targa_automezzo: ed.trasportatore?.targa_automezzo || prev.targa_automezzo
        }));
        
        // Cerca o suggerisci trasportatore (con controllo array vuoto)
        if (ed.trasportatore?.denominazione && trasportatori && trasportatori.length > 0) {
          const searchTerm = ed.trasportatore.denominazione.toLowerCase().split(' ')[0];
          const existingTrasp = trasportatori.find(t => 
            (t.denominazione && t.denominazione.toLowerCase().includes(searchTerm)) ||
            (t.codice_fiscale && t.codice_fiscale === ed.trasportatore.codice_fiscale)
          );
          if (existingTrasp) {
            setFormPrelievo(prev => ({...prev, trasportatore_id: existingTrasp.id}));
          }
        }
        
        // Cerca o suggerisci destinatario (con controllo array vuoto)
        if (ed.destinatario?.denominazione && destinatari && destinatari.length > 0) {
          const searchTerm = ed.destinatario.denominazione.toLowerCase().split(' ')[0];
          const existingDest = destinatari.find(d => 
            (d.denominazione && d.denominazione.toLowerCase().includes(searchTerm)) ||
            (d.codice_fiscale && d.codice_fiscale === ed.destinatario.codice_fiscale)
          );
          if (existingDest) {
            setFormPrelievo(prev => ({...prev, destinatario_id: existingDest.id}));
          }
        }
        
        // Chiudi modal scan e apri modal prelievo
        setShowModal('prelievo');
        setScanPreview(null);
        setScanningFir(false);
      } else {
        setScanError(data.error || data.detail || 'Impossibile estrarre i dati dal documento');
      }
    } catch (error) {
      console.error('Errore OCR:', error);
      setScanError(`Errore durante la scansione: ${error.message || 'Errore sconosciuto'}`);
    } finally {
      setScanningFir(false);
    }
  };

  // Formatta data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <RefreshCw className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header con statistiche */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
          <div className="text-orange-100 text-xs font-medium">Prelievi {statistiche?.anno}</div>
          <div className="text-2xl font-bold">{statistiche?.prelievi_anno || 0}</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-4 text-white">
          <div className="text-amber-100 text-xs font-medium">Kg {statistiche?.anno}</div>
          <div className="text-2xl font-bold">{statistiche?.kg_anno?.toFixed(1) || 0}</div>
        </div>
        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl p-4 text-white">
          <div className="text-cyan-100 text-xs font-medium">Trasportatori</div>
          <div className="text-2xl font-bold">{statistiche?.trasportatori_attivi || 0}</div>
        </div>
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-4 text-white">
          <div className="text-teal-100 text-xs font-medium">Ultimo Prelievo</div>
          <div className="text-lg font-bold">
            {statistiche?.ultimo_prelievo?.data_prelievo 
              ? formatDate(statistiche.ultimo_prelievo.data_prelievo)
              : '-'}
          </div>
        </div>
      </div>

      <BannerVerifiche aggiorna={aggiorna} onApri={() => setActiveTab('verifiche')} />

      {/* Info RENTRI */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
          <div>
            <p className="text-blue-900 font-semibold text-sm">Preparazione RENTRI</p>
            <p className="text-blue-700 text-xs mt-1">
              Codice EER 200125 - Oli e grassi commestibili. 
              I dati sono pronti per l'export verso il portale RENTRI.
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`oli-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-orange-100 text-orange-700'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Prelievi */}
      {activeTab === 'prelievi' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center gap-2">
            <h3 className="font-semibold text-gray-900">Registro Prelievi</h3>
            <div className="flex gap-2">
              <label className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white rounded-lg text-sm font-medium cursor-pointer shadow-md">
                <Camera className="h-4 w-4" />
                <span className="hidden sm:inline">Scansiona FIR</span>
                <Sparkles className="h-3 w-3" />
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      setShowModal('scan-fir');
                      handleScanFir(e.target.files[0]);
                    }
                  }}
                  data-testid="scan-fir-input"
                />
              </label>
              <button
                onClick={() => setShowModal('prelievo')}
                className="flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-medium"
                data-testid="nuovo-prelievo-btn"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Nuovo</span>
              </button>
            </div>
          </div>
          
          {prelievi.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <Droplet className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nessun prelievo registrato</p>
              <p className="text-sm">Clicca "Nuovo Prelievo" per iniziare</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="oli-registro-ritiri">
              {prelievi.map((prelievo) => (
                <RitiroCard key={prelievo.id} prelievo={prelievo} responsabile={responsabile}
                  onFir={setShowDocumento} onCaricaFir={(p, file) => uploadDocumentoFir(p.id, file)}
                  onCopia={setCopiaPer} onCorreggi={setCorreggiPer} onAnnulla={setAnnullaPer} />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'friggitrici' && (
        <RegistroFriggitrici responsabile={responsabile} onCambiato={() => setAggiorna((a) => a + 1)} />
      )}

      {activeTab === 'verifiche' && <VerificheOli aggiorna={aggiorna} />}

      {/* TAB: Trasportatori */}
      {activeTab === 'trasportatori' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Trasportatori Autorizzati</h3>
            <button
              onClick={() => {
                setEditingItem(null);
                setFormTrasportatore(TRASPORTATORE_VUOTO);
                setShowModal('trasportatore');
              }}
              className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium"
              data-testid="oli-trasportatore-nuovo"
            >
              <Plus className="h-4 w-4" />
              Nuovo
            </button>
          </div>
          
          {trasportatori.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <Truck className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nessun trasportatore registrato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {trasportatori.map((t) => (
                <div 
                  key={t.id}
                  className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm"
                  data-testid={`oli-trasportatore-${t.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{t.denominazione}</p>
                      <p className="text-sm text-gray-500">CF: {t.codice_fiscale}</p>
                      <p className="text-sm text-cyan-600">Albo: {t.numero_iscrizione_albo}{t.albo_categoria ? ` · cat. ${t.albo_categoria}` : ''}</p>
                      <div className="mt-1"><BadgeAutorizzazione scadenza={t.albo_scadenza} testid={`oli-trasportatore-scadenza-${t.id}`} /></div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingItem(t);
                          setFormTrasportatore(perModifica(TRASPORTATORE_VUOTO, t));
                          setShowModal('trasportatore');
                        }}
                        className="p-2 text-gray-500 hover:bg-gray-50 rounded-lg"
                        data-testid={`oli-trasportatore-modifica-${t.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => eliminaTrasportatore(t.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                        data-testid={`oli-trasportatore-elimina-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: Destinatari */}
      {activeTab === 'destinatari' && (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Impianti di destinazione</h3>
            <button
              onClick={() => { setEditingItem(null); setFormDestinatario(DESTINATARIO_VUOTO); setShowModal('destinatario'); }}
              className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium"
              data-testid="oli-impianto-nuovo"
            >
              <Plus className="h-4 w-4" />
              Nuovo
            </button>
          </div>
          
          {destinatari.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nessun destinatario registrato</p>
            </div>
          ) : (
            <div className="space-y-2">
              {destinatari.map((d) => (
                <div 
                  key={d.id}
                  className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm flex items-start justify-between gap-2"
                  data-testid={`oli-impianto-${d.id}`}
                >
                  <div className="min-w-0">
                    <p className="font-semibold text-gray-900">{d.denominazione}</p>
                    <p className="text-sm text-gray-500">{d.indirizzo}, {d.comune} ({d.provincia})</p>
                    <p className="text-sm text-teal-600">Operazione: {d.operazione_recupero}</p>
                    <p className="text-sm text-gray-600">
                      Autorizzazione: {d.numero_autorizzazione || <span className="text-red-600">da inserire</span>}
                      {d.autorizzazione_ente ? ` · ${d.autorizzazione_ente}` : ''}
                    </p>
                    <div className="mt-1"><BadgeAutorizzazione scadenza={d.autorizzazione_scadenza} testid={`oli-impianto-scadenza-${d.id}`} /></div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setEditingItem(d); setFormDestinatario(perModifica(DESTINATARIO_VUOTO, d)); setShowModal('destinatario'); }}
                      className="p-2 text-gray-500 hover:bg-gray-50 rounded-lg"
                      data-testid={`oli-impianto-modifica-${d.id}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => disattivaImpianto(d.id)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      title="Disattiva"
                      data-testid={`oli-impianto-disattiva-${d.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB: Statistiche */}
      {activeTab === 'statistiche' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Statistiche Olio Esausto</h3>
            <select
              value={statsAnno}
              onChange={(e) => setStatsAnno(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              {[2026, 2025, 2024, 2023].map(anno => (
                <option key={anno} value={anno}>{anno}</option>
              ))}
            </select>
          </div>

          {loadingStats ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
            </div>
          ) : statsDettaglio ? (
            <div className="space-y-4">
              {/* Totali Anno */}
              <div className="bg-gradient-to-br from-orange-500 to-amber-600 rounded-xl p-4 text-white">
                <h4 className="text-orange-100 text-sm font-medium mb-2">Totale {statsAnno}</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold">{statsDettaglio.totali?.prelievi || 0}</div>
                    <div className="text-orange-100 text-xs">Prelievi</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold">{statsDettaglio.totali?.kg?.toFixed(1) || 0}</div>
                    <div className="text-orange-100 text-xs">Kg Totali</div>
                  </div>
                </div>
              </div>

              {/* Grafico per Mese */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Andamento Mensile</h4>
                {statsDettaglio.per_mese && statsDettaglio.per_mese.length > 0 ? (
                  <div className="space-y-2">
                    {statsDettaglio.per_mese.map((mese) => (
                      <div key={mese.mese} className="flex items-center gap-3">
                        <div className="w-20 text-sm text-gray-600">{mese.nome_mese?.substring(0, 3)}</div>
                        <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-orange-400 to-amber-500 h-full rounded-full flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.min(100, (mese.kg_totali / (statsDettaglio.totali?.kg || 1)) * 100)}%`,
                              minWidth: mese.kg_totali > 0 ? '40px' : '0'
                            }}
                          >
                            {mese.kg_totali > 0 && (
                              <span className="text-xs text-white font-medium">{mese.kg_totali} kg</span>
                            )}
                          </div>
                        </div>
                        <div className="w-16 text-right text-sm text-gray-500">{mese.prelievi} prel.</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">Nessun dato per questo anno</p>
                )}
              </div>

              {/* Per Trasportatore */}
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Per Trasportatore</h4>
                {statsDettaglio.per_trasportatore && statsDettaglio.per_trasportatore.length > 0 ? (
                  <div className="space-y-2">
                    {statsDettaglio.per_trasportatore.map((trasp, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Truck className="h-5 w-5 text-teal-600" />
                          <span className="font-medium text-gray-900">{trasp.denominazione}</span>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-orange-600">{trasp.kg_totali} kg</div>
                          <div className="text-xs text-gray-500">{trasp.prelievi} prelievi</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm text-center py-4">Nessun trasportatore</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Nessuna statistica disponibile</p>
            </div>
          )}
        </div>
      )}

      {/* MODAL: Nuovo Prelievo */}
      {showModal === 'prelievo' && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[130]">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-orange-50 to-amber-50 sticky top-0">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">Nuovo Prelievo</h3>
                <button onClick={() => setShowModal(null)} className="p-1.5 hover:bg-white/50 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Prelievo</label>
                  <input
                    type="date"
                    value={formPrelievo.data_prelievo}
                    onChange={(e) => setFormPrelievo({...formPrelievo, data_prelievo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantità (kg)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={formPrelievo.quantita_kg}
                    onChange={(e) => setFormPrelievo({...formPrelievo, quantita_kg: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="es: 25.5"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trasportatore</label>
                <select
                  value={formPrelievo.trasportatore_id}
                  onChange={(e) => setFormPrelievo({...formPrelievo, trasportatore_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Seleziona...</option>
                  {trasportatori.map(t => (
                    <option key={t.id} value={t.id}>{t.denominazione}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Destinatario</label>
                <select
                  value={formPrelievo.destinatario_id}
                  onChange={(e) => setFormPrelievo({...formPrelievo, destinatario_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">Seleziona...</option>
                  {destinatari.map(d => (
                    <option key={d.id} value={d.id}>{d.denominazione}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numero FIR</label>
                <input
                  type="text"
                  value={formPrelievo.numero_fir}
                  onChange={(e) => setFormPrelievo({...formPrelievo, numero_fir: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="es: RYZMT 000568 GY"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Conducente Nome</label>
                  <input
                    type="text"
                    value={formPrelievo.conducente_nome}
                    onChange={(e) => setFormPrelievo({...formPrelievo, conducente_nome: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                  <input
                    type="text"
                    value={formPrelievo.conducente_cognome}
                    onChange={(e) => setFormPrelievo({...formPrelievo, conducente_cognome: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Targa Automezzo</label>
                <input
                  type="text"
                  value={formPrelievo.targa_automezzo}
                  onChange={(e) => setFormPrelievo({...formPrelievo, targa_automezzo: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="es: GTO67FA"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={formPrelievo.note}
                  onChange={(e) => setFormPrelievo({...formPrelievo, note: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  rows={2}
                />
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100 space-y-2">
              <button
                onClick={salvaPrelievo}
                disabled={!formPrelievo.trasportatore_id || !formPrelievo.destinatario_id || !formPrelievo.quantita_kg}
                className="w-full py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 text-white rounded-xl font-medium"
                data-testid="oli-prelievo-salva"
              >
                Salva Prelievo
              </button>
              <button
                onClick={() => setShowModal(null)}
                className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nuovo/Modifica Trasportatore */}
      {showModal === 'trasportatore' && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[130]">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-cyan-50 to-blue-50 sticky top-0">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">
                  {editingItem ? 'Modifica' : 'Nuovo'} Trasportatore
                </h3>
                <button onClick={() => setShowModal(null)} className="p-1.5 hover:bg-white/50 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Denominazione *</label>
                <input
                  type="text"
                  value={formTrasportatore.denominazione}
                  onChange={(e) => setFormTrasportatore({...formTrasportatore, denominazione: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="es: LUMAOIL S.R.L.S."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale *</label>
                <input
                  type="text"
                  value={formTrasportatore.codice_fiscale}
                  onChange={(e) => setFormTrasportatore({...formTrasportatore, codice_fiscale: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N. Iscrizione Albo *</label>
                <input
                  type="text"
                  value={formTrasportatore.numero_iscrizione_albo}
                  onChange={(e) => setFormTrasportatore({...formTrasportatore, numero_iscrizione_albo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  placeholder="es: NA/24825"
                  data-testid="oli-trasportatore-albo"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Categoria Albo</label>
                  <input
                    type="text"
                    value={formTrasportatore.albo_categoria}
                    onChange={(e) => setFormTrasportatore({...formTrasportatore, albo_categoria: e.target.value})}
                    className={CAMPO_DATA}
                    placeholder="es: 4 classe F"
                    data-testid="oli-trasportatore-categoria"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Iscrizione valida fino al</label>
                  <input
                    type="date"
                    value={formTrasportatore.albo_scadenza}
                    onChange={(e) => setFormTrasportatore({...formTrasportatore, albo_scadenza: e.target.value})}
                    className={CAMPO_DATA}
                    data-testid="oli-trasportatore-scadenza"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={formTrasportatore.telefono}
                    onChange={(e) => setFormTrasportatore({...formTrasportatore, telefono: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formTrasportatore.email}
                    onChange={(e) => setFormTrasportatore({...formTrasportatore, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100 space-y-2">
              <button
                onClick={salvaTrasportatore}
                disabled={!formTrasportatore.denominazione || !formTrasportatore.codice_fiscale || !formTrasportatore.numero_iscrizione_albo}
                className="w-full py-3 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-300 text-white rounded-xl font-medium"
                data-testid="oli-trasportatore-salva"
              >
                Salva
              </button>
              <button
                onClick={() => setShowModal(null)}
                className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nuovo Destinatario */}
      {showModal === 'destinatario' && (
        <div className="fixed inset-0 bg-black/70 flex items-end lg:items-center justify-center z-[130]">
          <div className="bg-white rounded-t-2xl lg:rounded-2xl w-full lg:max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-teal-50 to-emerald-50 sticky top-0">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-gray-900">{editingItem ? 'Modifica impianto' : 'Nuovo impianto'}</h3>
                <button onClick={() => setShowModal(null)} className="p-1.5 hover:bg-white/50 rounded-lg">
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Denominazione *</label>
                <input
                  type="text"
                  value={formDestinatario.denominazione}
                  onChange={(e) => setFormDestinatario({...formDestinatario, denominazione: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="es: MY GREEN S.R.L."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale *</label>
                <input
                  type="text"
                  value={formDestinatario.codice_fiscale}
                  onChange={(e) => setFormDestinatario({...formDestinatario, codice_fiscale: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo *</label>
                <input
                  type="text"
                  value={formDestinatario.indirizzo}
                  onChange={(e) => setFormDestinatario({...formDestinatario, indirizzo: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="es: Via Mola Bracaglia SNC"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comune *</label>
                  <input
                    type="text"
                    value={formDestinatario.comune}
                    onChange={(e) => setFormDestinatario({...formDestinatario, comune: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provincia *</label>
                  <input
                    type="text"
                    value={formDestinatario.provincia}
                    onChange={(e) => setFormDestinatario({...formDestinatario, provincia: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    maxLength={2}
                    placeholder="es: FR"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">N. Autorizzazione</label>
                <input
                  type="text"
                  value={formDestinatario.numero_autorizzazione}
                  onChange={(e) => setFormDestinatario({...formDestinatario, numero_autorizzazione: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="es: 96A.216.FR"
                  data-testid="oli-impianto-autorizzazione"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rilasciata da</label>
                  <input
                    type="text"
                    value={formDestinatario.autorizzazione_ente}
                    onChange={(e) => setFormDestinatario({...formDestinatario, autorizzazione_ente: e.target.value})}
                    className={CAMPO_DATA}
                    placeholder="es: Regione Lazio"
                    data-testid="oli-impianto-ente"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valida fino al</label>
                  <input
                    type="date"
                    value={formDestinatario.autorizzazione_scadenza}
                    onChange={(e) => setFormDestinatario({...formDestinatario, autorizzazione_scadenza: e.target.value})}
                    className={CAMPO_DATA}
                    data-testid="oli-impianto-scadenza"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operazione Recupero</label>
                <select
                  value={formDestinatario.operazione_recupero}
                  onChange={(e) => setFormDestinatario({...formDestinatario, operazione_recupero: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="R13">R13 - Messa in riserva</option>
                  <option value="R9">R9 - Rigenerazione oli</option>
                  <option value="R3">R3 - Riciclo sostanze organiche</option>
                </select>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-100 space-y-2">
              <button
                onClick={salvaDestinatario}
                disabled={!formDestinatario.denominazione || !formDestinatario.codice_fiscale || !formDestinatario.indirizzo || !formDestinatario.comune || !formDestinatario.provincia}
                className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white rounded-xl font-medium"
                data-testid="oli-impianto-salva"
              >
                Salva
              </button>
              <button
                onClick={() => setShowModal(null)}
                className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm"
              >
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Scansione FIR (OCR) */}
      {showModal === 'scan-fir' && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[130]">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                  <h3 className="font-bold text-gray-900">Scansione FIR con AI</h3>
                </div>
                <button 
                  onClick={() => {
                    setShowModal(null);
                    setScanPreview(null);
                    setScanError(null);
                    setScanningFir(false);
                  }} 
                  className="p-1.5 hover:bg-white/50 rounded-lg"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {scanPreview && (
                <div className="mb-4">
                  <img 
                    src={scanPreview} 
                    alt="FIR Preview" 
                    className="w-full h-48 object-contain rounded-lg border border-gray-200"
                  />
                </div>
              )}
              
              {scanningFir ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                    <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />
                  </div>
                  <p className="text-gray-700 font-medium">Analisi documento in corso...</p>
                  <p className="text-gray-500 text-sm mt-2">L'AI sta estraendo i dati dal FIR</p>
                </div>
              ) : scanError ? (
                <div className="text-center py-6">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 mb-4">
                    <AlertCircle className="h-7 w-7 text-red-600" />
                  </div>
                  <p className="text-red-600 font-medium">{scanError}</p>
                  <label className="inline-flex items-center gap-2 px-4 py-2 mt-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium cursor-pointer">
                    <Camera className="h-4 w-4" />
                    Riprova
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleScanFir(e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                </div>
              ) : (
                <div className="text-center py-6">
                  <label className="inline-flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-purple-300 rounded-xl cursor-pointer hover:bg-purple-50 transition-colors">
                    <Camera className="h-12 w-12 text-purple-400 mb-3" />
                    <span className="text-purple-600 font-medium">Fotografa o carica FIR</span>
                    <span className="text-gray-400 text-sm mt-1">JPG, PNG, WEBP</span>
                    <input
                      type="file"
                      className="hidden"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        if (e.target.files[0]) {
                          handleScanFir(e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Visualizza Documento FIR */}
      {showDocumento && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[140]" onClick={() => setShowDocumento(null)}>
          <div className="relative w-full h-full flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-black/50 text-white p-4 flex items-center justify-between">
              <div>
                <h3 className="font-bold">Documento FIR</h3>
                <p className="text-sm text-gray-300">
                  {showDocumento.numero_fir} - {formatDate(showDocumento.data_prelievo)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={urlDocumento(showDocumento.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2"
                  data-testid="oli-fir-scarica"
                >
                  <Download className="h-4 w-4" />
                  Scarica
                </a>
                <button 
                  onClick={() => setShowDocumento(null)}
                  className="p-2 hover:bg-white/20 rounded-lg"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Immagine */}
            <div className="flex-1 overflow-auto flex items-center justify-center p-4">
              {showDocumento.documento_fir?.content_type === 'application/pdf' ? (
                <iframe src={urlDocumento(showDocumento.id)} title="FIR" className="w-full h-full bg-white rounded-lg" data-testid="oli-fir-pdf" />
              ) : (
                <img
                  src={urlDocumento(showDocumento.id)}
                  alt={`FIR ${showDocumento.numero_fir}`}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                  data-testid="oli-fir-immagine"
                />
              )}
            </div>
            
            {/* Info prelievo */}
            <div className="bg-black/50 text-white p-4">
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Quantità:</span>
                  <span className="ml-2 font-bold">{showDocumento.quantita_kg} kg</span>
                </div>
                <div>
                  <span className="text-gray-400">Trasportatore:</span>
                  <span className="ml-2">{trasportatori.find(t => t.id === showDocumento.trasportatore_id)?.denominazione || '-'}</span>
                </div>
                <div>
                  <span className="text-gray-400">Conducente:</span>
                  <span className="ml-2">{showDocumento.conducente_nome} {showDocumento.conducente_cognome}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <CopiaFirmataDialog prelievo={copiaPer} onClose={() => setCopiaPer(null)} onSaved={() => { setCopiaPer(null); loadData(); }} />
      <CorreggiRitiroDialog prelievo={correggiPer} trasportatori={trasportatori} destinatari={destinatari}
        onClose={() => setCorreggiPer(null)} onSaved={() => { setCorreggiPer(null); loadData(); }} />
      <AnnullaDialog open={!!annullaPer} titolo={`Annulla ritiro n. ${annullaPer?.numero_registro || ''}`}
        descrizione="Il ritiro non si cancella: resta nel registro come annullato, con il motivo, chi l'ha annullato e quando."
        onClose={() => setAnnullaPer(null)}
        onConferma={async (motivo) => { await oliApi.post(`/prelievi/${annullaPer.id}/annulla`, { motivo }); toast.success('Ritiro annullato'); loadData(); }} />
    </div>
  );
};

export default OliEsaustiSection;
